import { useEffect, useRef, useState } from 'react'
import { Box, CssBaseline, ThemeProvider, createTheme } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import Beacon4FilterBar              from './components/filters/Beacon4FilterBar'
import Beacon4PerformanceNetworkOverviewLegendPanel from './components/filters/Beacon4PerformanceNetworkOverviewLegendPanel'
import Beacon4PerformanceNetworkOverviewView         from './components/Beacon4PerformanceNetworkOverviewView'
import Beacon4ExportButton           from './components/Beacon4ExportButton'
import { selectBeacon4LegendVisible } from './reducers/Beacon4FiltersReducer'
import { loadBeacon4PerformanceNetworkOverviewAvailableKpis } from './reducers/Beacon4PerformanceNetworkOverviewKpiDataReducer'

const theme = createTheme({
  typography: { fontFamily: "'IBM Plex Sans', sans-serif" },
  palette:    { primary: { main: '#1565c0' }, background: { default: '#f0f2f5' } },
})

// Default/bounds for the user-resizable legend panel (in pixels).
const LEGEND_MIN_W = 160
const LEGEND_MAX_W = 420
const LEGEND_DEFAULT_W = 220

/**
 * Resize handle placed on the boundary between the chart/table column and
 * the legend panel. Dragging it directly controls the LEGEND's width —
 * dragging left (toward the charts) grows the legend, dragging right
 * shrinks it. Mirrors the ChartResizeHandle in
 * Beacon4PerformanceNetworkOverviewView (same plain, always-visible grey
 * bar; double-click resets to default).
 */
function LegendResizeHandle({ onResize, onReset }) {
  const draggingRef = useRef(false)
  const lastXRef     = useRef(0)
  const [dragging, setDragging] = useState(false)

  useEffect(() => {
    const onMouseMove = (e) => {
      if (!draggingRef.current) return
      const delta = e.clientX - lastXRef.current
      lastXRef.current = e.clientX
      // Dragging LEFT (delta < 0) grows the legend; dragging RIGHT shrinks
      // it — the legend sits on the right edge, so moving the handle left
      // takes width from the chart area and gives it to the legend.
      onResize(w => Math.min(LEGEND_MAX_W, Math.max(LEGEND_MIN_W, w - delta)))
    }
    const onMouseUp = () => {
      if (!draggingRef.current) return
      draggingRef.current = false
      setDragging(false)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('mouseup', onMouseUp)
    return () => {
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('mouseup', onMouseUp)
    }
  }, [onResize])

  const onMouseDown = (e) => {
    draggingRef.current = true
    setDragging(true)
    lastXRef.current = e.clientX
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
  }

  return (
    <Box
      onMouseDown={onMouseDown}
      onDoubleClick={onReset}
      title="Drag to resize legend · double-click to reset"
      sx={{
        flexShrink: 0,
        width: 6,
        mx: 0.75,
        borderRadius: 1,
        cursor: 'col-resize',
        bgcolor: dragging ? 'grey.400' : 'grey.200',
        '&:hover': { bgcolor: 'grey.400' },
      }}
    />
  )
}

export default function App({ externalRole }) {
  // Wraps BOTH the chart/table area AND the legend panel — not just the
  // chart area. Beacon4ExportButton captures whatever this ref points to;
  // if it only wrapped the chart column, the legend Drawer (a sibling, not
  // a descendant, of that column) would never be included in the page-level
  // export, leaving the exported charts with colored lines nobody can
  // decode. No changes needed inside Beacon4ExportButton itself — it just
  // needs a container ref that actually contains everything.
  const exportAreaRef = useRef(null)
  const dispatch       = useDispatch()
  const legendVisible  = useSelector(selectBeacon4LegendVisible)

  const [legendW, setLegendW] = useState(LEGEND_DEFAULT_W)
  const resetLegendWidth = () => setLegendW(LEGEND_DEFAULT_W)

  // Load the techs + main-KPIs lookup once on mount. The backend caches this
  // for 5 min, so re-mounts are cheap.
  useEffect(() => {
    dispatch(loadBeacon4PerformanceNetworkOverviewAvailableKpis())
  }, [dispatch])

  // Try to fill the height the host shell gives us. If the shell's
  // container doesn't define a height, height: 100% collapses to 0,
  // so we also set a minHeight based on the viewport minus an estimate
  // of the shell's chrome (~120px for topbar + a bit of breathing room).
  // Adjust the constant below if your shell is taller/shorter.
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{
        height: '100%',
        minHeight: 'calc(100vh - 120px)',
        display: 'flex', flexDirection: 'column',
        bgcolor: 'background.default',
      }}>
        <Box sx={{ flexShrink: 0 }}>
          <Beacon4FilterBar
            exportButton={<Beacon4ExportButton containerRef={exportAreaRef} />}
            externalRole={externalRole}
          />
        </Box>

        {/* Body = remaining height. minHeight: 0 lets it shrink properly
            inside the flex column. This whole row (chart/table + legend
            panel) is what gets captured for the page-level export. */}
        <Box ref={exportAreaRef} sx={{ flex: 1, minHeight: 0, display: 'flex', overflow: 'hidden' }}>
          {/* Chart area — scroll is handed over to
              Beacon4PerformanceNetworkOverviewView itself (it sizes the
              table via autoHeight and scrolls internally). */}
          <Box
            sx={{
              flex: 1, minWidth: 0, minHeight: 0,
              p: '12px 16px',
              overflowY: 'hidden',
              display: 'flex', flexDirection: 'column',
            }}
          >
            <Beacon4PerformanceNetworkOverviewView externalRole={externalRole} />
          </Box>
          {/* Legend + its resize handle are both hidden together when the
              "Legend" toggle (FilterBar > Options) is off — no point keeping
              a handle to resize a panel that isn't there. Hiding it just
              lets the chart/table Box (flex: 1 above) reclaim the width. */}
          {legendVisible && (
            <>
              <LegendResizeHandle onResize={setLegendW} onReset={resetLegendWidth} />
              <Beacon4PerformanceNetworkOverviewLegendPanel width={legendW} />
            </>
          )}
        </Box>
      </Box>
    </ThemeProvider>
  )
}
